import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import { PersonalFlowPage, type PersonalFlowTab } from '@pages/student';
import { useAbility } from '@features/authorization';
import { AuthLoading, useAuth } from '@features/auth';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessPersonalFlow } from '@shared/lib';

export const personalFlowSearchSchema = z.object({
  tab: z.enum(['in-progress', 'completed', 'settings']).catch('in-progress'),
});

export function enforcePersonalFlowRouteAccess({
  context,
  params,
}: {
  context: RouterContext;
  params: { locale: string };
}): void {
  const { ability, auth } = context;

  if (auth.isLoading) return;

  if (canAccessPersonalFlow(ability, auth.user?.id ?? null)) return;

  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({
    to: '/$locale/unauthorized',
    params: { locale: params.locale },
  });
}

export const Route = createFileRoute('/$locale/_authenticated/student/personal-flow')({
  component: PersonalFlowRoute,
  validateSearch: personalFlowSearchSchema,
  beforeLoad: ({ context, params }) => {
    enforcePersonalFlowRouteAccess({
      context: context as RouterContext,
      params,
    });
  },
});

export function PersonalFlowRoute() {
  const navigate = useNavigate();
  const { locale } = Route.useParams();
  const { tab } = Route.useSearch();
  const auth = useAuth();
  const ability = useAbility();
  const hasAccess = canAccessPersonalFlow(ability, auth.user?.id ?? null);

  useEffect(() => {
    if (auth.isLoading) return;

    if (!auth.isAuthenticated) {
      void navigate({ to: '/$locale/login', params: { locale }, replace: true });
      return;
    }

    if (!hasAccess) {
      void navigate({ to: '/$locale/unauthorized', params: { locale }, replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, hasAccess, locale, navigate]);

  if (auth.isLoading) {
    return <AuthLoading />;
  }

  if (!auth.isAuthenticated) {
    return <AuthLoading message="Redirecting to login..." />;
  }

  if (!hasAccess) {
    return <AuthLoading message="Redirecting to unauthorized..." />;
  }

  return (
    <PersonalFlowPage
      locale={locale}
      activeTab={tab}
      onTabChange={(nextTab: PersonalFlowTab) => {
        void navigate({
          from: Route.fullPath,
          search: { tab: nextTab },
          replace: true,
        });
      }}
    />
  );
}
